/* eslint-disable no-underscore-dangle */
import {
  BaseField,
  DisplayIf,
  Field,
  FormLayout,
  HookForm,
} from '@/components_v2/HookForm';
import { QuestionTypes, TriggerableTypes } from '@/gql/graphql';
import { useModals } from '@/hooks/ModalManager';
import { Button, chakra, Divider } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  AutomationPropertySelect,
  QuestionSelect,
  UserSelect,
} from '../../Selects';
import { actionSchema, ActionSchema } from '../form-schemas';
import { Callout } from '../misc';

const getActionableTypes = (t: any) => [
  { value: '', label: t('placeholders.select') },
  { value: 'email_checklist', label: t('form_types.send_email') },
  { value: 'email_user', label: t('form_types.send_email_user') },
  { value: 'webhook', label: 'WebHook' },
  { value: 'update_property', label: t('form_types.update_ticket_property') },
];

function Fields({ automationFlowId }: { automationFlowId?: string }) {
  const { t } = useTranslation();
  const form = useFormContext<ActionSchema>();
  const actionableTypes = getActionableTypes(t);

  return (
    <FormLayout padding={1} spacing={5} flexGrow={0} w="full" overflow="visible">
      <Field<ActionSchema>
        name="actionableType"
        label={t('form_labels.activation_type')}
        type="native-select"
        defaultValue=""
        options={actionableTypes}
      />
      <DisplayIf<ActionSchema>
        name="actionableType"
        condition={(value) => !!value}
      >
        <Field<ActionSchema>
          name="label"
          label={t('form_labels.label')}
          type="text"
          placeholder={t('placeholders.type_dots')}
          help={t('form_labels.label_help_action')}
        />
      </DisplayIf>

      <chakra.div>
        <DisplayIf<ActionSchema>
          name="actionableType"
          condition={(value) => value === 'email_checklist'}
        >
          <FormLayout
            padding={1}
            spacing={5}
            flexGrow={0}
            w="full"
          >
            <Divider />
            <Callout>
              <chakra.p fontSize="xl" fontWeight="bold">
                {t('tickets_module.email_how_to_use')}
              </chakra.p>
              {t('tickets_module.email_description')}
            </Callout>
            <Controller
              name="emailAttribute"
              control={form.control}
              shouldUnregister
              render={({ field }) => (
                <BaseField
                  name="triggerable"
                  label={t('form_labels.search_email_question')}
                  flexGrow={0}
                >
                  <QuestionSelect
                    selectionMode="single"
                    questionTypes={[
                      QuestionTypes.Text,
                      QuestionTypes.Radio,
                      QuestionTypes.RadioCombo,
                    ]}
                    value={field.value?.map((e) => e?.id) as any}
                    initialOptions={(field.value || []) as any}
                    onChange={(keys) => {
                      field.onChange(keys);
                    }}
                  />
                </BaseField>
              )}
            />
            <Field<ActionSchema>
              name="sendChecklistOpenFlow"
              label={t('form_labels.attach_checklist_started_step')}
              type="switch"
              defaultValue=""
              options={actionableTypes}
            />
          </FormLayout>
        </DisplayIf>

        <DisplayIf<ActionSchema>
          name="actionableType"
          condition={(value) => value === 'email_user'}
        >
          <FormLayout
            padding={1}
            spacing={5}
            flexGrow={0}
            w="full"
          >
            <Controller
              name="user"
              control={form.control}
              shouldUnregister
              render={({ field }) => (
                <BaseField
                  name="triggerable"
                  label={t('form_labels.select_user_email')}
                  flexGrow={0}
                >
                  <UserSelect
                    selectionMode="single"
                    value={field.value?.map((e) => e?.id) as any}
                    initialOptions={(field.value || []) as any}
                    onChange={(keys) => {
                      field.onChange(keys);
                    }}
                  />
                </BaseField>
              )}
            />
            <Field<ActionSchema>
              name="sendChecklistOpenFlow"
              label={t('form_labels.attach_checklist_started_step')}
              type="switch"
              defaultValue=""
              options={actionableTypes}
            />
          </FormLayout>
        </DisplayIf>

        <DisplayIf<ActionSchema>
          name="actionableType"
          condition={(value) => value === 'webhook'}
        >
          <FormLayout
            padding={1}
            spacing={5}
            flexGrow={0}
            w="full"
          >
            <Divider />
            <Callout>
              <chakra.p fontSize="xl" fontWeight="bold">
                {t('tickets_module.webhook_title')}
              </chakra.p>
              {t('tickets_module.webhook_description')}
            </Callout>
            <Field<ActionSchema>
              help={t('form_labels.webhook_url_help')}
              name="webhookUrl"
              label={t('form_labels.webhook_url')}
              type="text"
            />
          </FormLayout>
        </DisplayIf>

        <DisplayIf<ActionSchema>
          name="actionableType"
        >
          <FormLayout spacing={5} flexGrow={0} w="full">
            <Divider />
            <Callout>
              <chakra.p fontSize="xl" fontWeight="bold">
                {t('tickets_module.update_property_title')}
              </chakra.p>
              {t('tickets_module.update_property_description', {
                property: t('tickets_module.property'),
                question: t('tickets_module.question'),
              })}
            </Callout>

            <Controller
              name="attribute"
              control={form.control}
              render={({ field }) => (
                <BaseField
                  name="attribute"
                  label={t('form_labels.update_ticket_property_label')}
                  flexGrow={0}
                >
                  <AutomationPropertySelect
                    selectionMode="single"
                    automationFlowId={automationFlowId || ''}
                    value={field.value?.map((e) => e!.id)}
                    initialOptions={(field.value || []) as any}
                    onChange={(keys) => {
                      field.onChange(keys?.length ? keys : undefined);
                    }}
                  />
                </BaseField>
              )}
            />
            <Controller
              name="question"
              control={form.control}
              shouldUnregister
              render={({ field }) => (
                <BaseField
                  name="question"
                  label={t('form_labels.inherit_value_question')}
                  flexGrow={0}
                >
                  <QuestionSelect
                    selectionMode="single"
                    value={field.value?.map((e) => e?.id) as any}
                    initialOptions={(field.value || []) as any}
                    onChange={(keys) => {
                      field.onChange(keys);
                    }}
                  />
                </BaseField>
              )}
            />
          </FormLayout>
        </DisplayIf>
      </chakra.div>
    </FormLayout>
  );
}

export type ActionFormProps = {
  onSubmit?: (value: ActionSchema & { key?: string }) => void;
  intitialValues?: ActionSchema;
  automationFlowId?: string;
};

export function ActionForm({
  intitialValues,
  onSubmit,
  automationFlowId,
}: ActionFormProps) {
  return (
    <HookForm<ActionSchema>
      autoComplete="off"
      overflowY="auto"
      overflowX="clip"
      position="relative"
      p="4"
      display="grid"
      flexGrow={0}
      w="full"
      id="action-form"
      resolver={yupResolver(actionSchema)}
      schema={actionSchema}
      defaultValues={intitialValues}
      onSubmit={(values) => onSubmit?.(values as ActionSchema)}
    >
      <Fields automationFlowId={automationFlowId} />
    </HookForm>
  );
}

export function useActionForm({ onSubmit }: ActionFormProps) {
  const modals = useModals();
  const { t } = useTranslation();

  const _onSubmit = React.useCallback(
    (value: ActionSchema) => {
      onSubmit?.(value);
      modals.close('action-form');
    },
    [modals, onSubmit],
  );

  return (params: {
    intitialValues?: ActionSchema;
    automationFlowId?: string;
  }) => {
    modals.open({
      id: 'action-form',
      title: t('actions.configure_secondary_actions'),
      scope: 'alert',
      body: <ActionForm onSubmit={_onSubmit} {...params} />,
      size: 'lg',
      closeOnEsc: false,
      closeOnOverlayClick: false,
      footer: (
        <Button
          type="submit"
          form="action-form"
          w="full"
          size="md"
          colorScheme="brand"
        >
          {t('tickets_module.save')}
        </Button>
      ),
    });
  };
}
